/** Test Render — side-effect-free full-composition preview of a notification.
 *
 * Renders a notification's delivery methods against a test context through the
 * real delivery seams (block namespaces, CUSTOM/STANDARD templates, theme shell
 * wrap + signatures, language policy) without sending or writing anything, and
 * shows each channel's output next to its composition provenance. Thin
 * composition over the shared test-render pieces (`test-render/`), which the
 * notification editor's TestRenderDialog reuses.
 */
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  listTestRenderEvents,
  testRender,
  RENDER_CHANNELS,
  type SupportedEvent,
  type TestRenderResult,
} from '@/api/notificationTestRender';
import { PageHeader } from '@/components/PageHeader';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { ChannelResultCard } from './test-render/ChannelResultCard';
import {
  buildEvaluateContext,
  contextSourceComplete,
  ContextSourcePicker,
  EMPTY_CONTEXT_SOURCE,
  type ContextSourceValue,
} from './test-render/ContextSourcePicker';

type ChannelOption = (typeof RENDER_CHANNELS)[number];

export function TestRenderPage() {
  const { data: events = [] } = useQuery({
    queryKey: ['notification-test-render-events'],
    queryFn: listTestRenderEvents,
    staleTime: 5 * 60_000,
  });

  const [notificationId, setNotificationId] = useState('');
  const [deliveryMethodId, setDeliveryMethodId] = useState('');
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [event, setEvent] = useState<SupportedEvent | null>(null);
  const [source, setSource] = useState<ContextSourceValue>(EMPTY_CONTEXT_SOURCE);
  const [debugMarkers, setDebugMarkers] = useState(false);

  const entityKind = event?.entity_kind ?? null;

  const mutation = useMutation<TestRenderResult, unknown, void>({
    mutationFn: () =>
      testRender({
        notification: Number(notificationId),
        ...(deliveryMethodId.trim() ? { delivery_method: Number(deliveryMethodId) } : {}),
        ...(channels.length ? { channels: channels.map((c) => c.value) } : {}),
        ...(event ? { event: event.code } : {}),
        context: buildEvaluateContext(source),
        debug_markers: debugMarkers,
      }),
  });

  const requestError =
    mutation.error instanceof ConsoleApiError
      ? mutation.error.message
      : mutation.error
        ? 'Test render request failed.'
        : null;

  const canRender = notificationId.trim() !== '' && contextSourceComplete(source, entityKind);

  const out = mutation.data;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-test-render"
        title="Test Render"
        subtitle="Render a notification's delivery methods against a real context — full composition (blocks, layout, theme, language policy), nothing is sent or written."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 720 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              label="Notification ID"
              size="small"
              type="number"
              sx={{ width: 200 }}
              value={notificationId}
              onChange={(e) => setNotificationId(e.target.value)}
              helperText="Browse notifications in the operator's dashboard (Notifications editor) to find the id."
            />
            <TextField
              label="Delivery method ID"
              size="small"
              type="number"
              sx={{ width: 200 }}
              value={deliveryMethodId}
              onChange={(e) => setDeliveryMethodId(e.target.value)}
              helperText="Optional — one method (drafts allowed). Default: all enabled methods."
            />
          </Stack>

          <Autocomplete<ChannelOption, true>
            multiple
            size="small"
            options={RENDER_CHANNELS as unknown as ChannelOption[]}
            value={channels}
            onChange={(_, v) => setChannels(v as ChannelOption[])}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <TextField {...params} label="Channels" placeholder="All channels" />
            )}
          />

          <Autocomplete<SupportedEvent>
            size="small"
            options={events}
            value={event}
            onChange={(_, v) => setEvent(v)}
            getOptionLabel={(o) => o.code}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            groupBy={(o) => o.family}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Event"
                placeholder="Defaults to the notification's own event"
              />
            )}
          />

          <ContextSourcePicker
            value={source}
            onChange={setSource}
            entityKind={entityKind}
            entityFamily={event?.family}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={debugMarkers}
                onChange={(e) => setDebugMarkers(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Block boundary markers (&lt;!-- block:… --&gt; comments around each block value)
              </Typography>
            }
          />

          <Box>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!canRender || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? 'Rendering…' : 'Render'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {requestError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {requestError}
        </Alert>
      )}

      {out && (
        <Stack spacing={2} sx={{ pb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {!out.resolved && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Some lazy context fields could not be resolved — output may be incomplete.
              </Alert>
            )}
            {out.language && <Chip size="small" label={`Language: ${out.language}`} />}
            <Chip
              size="small"
              variant="outlined"
              label={`${out.results.length} channel${out.results.length === 1 ? '' : 's'}`}
            />
          </Stack>
          {out.results.map((res) => (
            <ChannelResultCard key={`${res.delivery_method}-${res.channel}`} res={res} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
